import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AlertCircle, CheckCircle2, MessageCircle, RefreshCw, ShieldCheck } from "lucide-react";
import { AuthShell } from "@/pages/Login";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const loadStoredActivation = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem("kd9-whatsapp-activation") || "null");
  } catch (_) {
    return null;
  }
};

export default function WhatsAppActivation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const activation = useMemo(
    () => location.state?.activationToken ? location.state : loadStoredActivation(),
    [location.state],
  );
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(activation?.message || "");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(Boolean(activation?.otpSent));
  const [deliveryStatus, setDeliveryStatus] = useState(activation?.deliveryStatus || (activation?.otpSent ? "sent" : "pending_retry"));
  const [cooldown, setCooldown] = useState(sent ? 60 : 0);
  const [expiresIn, setExpiresIn] = useState(Number(activation?.expiresInSeconds || 0));

  useEffect(() => {
    if (!cooldown) return undefined;
    const timer = window.setInterval(() => setCooldown((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  useEffect(() => {
    if (!expiresIn) return undefined;
    const timer = window.setInterval(() => setExpiresIn((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [expiresIn]);

  const resend = async () => {
    if (!activation?.activationToken || busy || cooldown > 0) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/api/auth/whatsapp-activation/request", {
        activation_token: activation.activationToken,
      });
      setSent(true);
      setDeliveryStatus(data?.delivery_status || "sent");
      setCooldown(60);
      setExpiresIn(Number(data?.expires_in_seconds || 0));
      setNotice(data?.verified
        ? "Tu WhatsApp ya estaba verificado."
        : "Nuevo código generado y enviado. Solo el código más reciente es válido.");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (event) => {
    event.preventDefault();
    if (!activation?.activationToken) return;
    if (!/^\d{6}$/.test(code)) {
      setError("Ingresa los 6 dígitos enviados a tu WhatsApp.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post("/api/auth/whatsapp-activation/confirm", {
        activation_token: activation.activationToken,
        code,
      });
      await refresh();
      window.sessionStorage.removeItem("kd9-whatsapp-activation");
      navigate(activation.destination || "/app/inicio", { replace: true });
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!activation?.activationToken) {
    return (
      <AuthShell>
        <div className="w-full max-w-md kd9-card p-6 text-center">
          <AlertCircle className="mx-auto mb-3" />
          <h1 className="font-cinzel text-2xl kd9-gold-text">Verificación no disponible</h1>
          <p className="mt-3 text-sm opacity-70">La sesión de activación venció o no existe. Inicia sesión para continuar la verificación.</p>
          <Link to="/login" className="kd9-btn-gold mt-5 inline-flex rounded-xl px-5 py-3">Ir a iniciar sesión</Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="w-full max-w-md kd9-fade-up" data-testid="whatsapp-activation">
        <div className="kd9-card p-6 md:p-8">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full border" style={{ borderColor: "var(--kd9-gold)" }}>
            <ShieldCheck size={26} style={{ color: "var(--kd9-gold)" }} />
          </div>
          <h1 className="text-center font-cinzel text-2xl md:text-3xl kd9-gold-text">Verifica tu WhatsApp</h1>
          <p className="mt-3 text-center text-sm opacity-70">
            KD9 utiliza esta validación para confirmar que el número pertenece a la persona que está creando la cuenta.
          </p>
          <div className="mt-5 rounded-xl border p-4 text-center text-sm" style={{ borderColor: "var(--kd9-border)" }}>
            <MessageCircle size={18} className="mx-auto mb-2" />
            {sent ? (
              <>Código enviado a <strong>{activation.phoneMasked || "tu WhatsApp"}</strong></>
            ) : (
              <>Cuenta creada. Falta enviar el código a <strong>{activation.phoneMasked || "tu WhatsApp"}</strong></>
            )}
            <div className="mt-2 text-xs opacity-60">
              {deliveryStatus === "sent" ? "Verificación automática activa" : "Envío pendiente · puedes intentarlo nuevamente"}
            </div>
          </div>

          {notice && (
            <div className="mt-4 rounded-xl border px-4 py-3 text-sm" style={{ borderColor: "var(--kd9-border)" }}>
              {notice}
            </div>
          )}
          {error && (
            <div className="mt-4 flex items-start gap-2 rounded-xl border px-4 py-3 text-sm" style={{ borderColor: "rgba(122,14,23,0.5)", color: "#FCA5A5" }}>
              <AlertCircle size={15} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={confirm} className="mt-6 space-y-4">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              className="kd9-input w-full text-center text-2xl tracking-[0.5em]"
              placeholder="000000"
              aria-label="Código de verificación de 6 dígitos"
              data-testid="whatsapp-activation-code"
            />
            <button
              type="submit"
              disabled={busy || code.length !== 6}
              className="kd9-btn-gold flex w-full items-center justify-center gap-2 rounded-xl py-4"
              data-testid="whatsapp-activation-confirm"
            >
              <CheckCircle2 size={17} /> {busy ? "Validando..." : "Validar y entrar a KD9"}
            </button>
          </form>

          <button
            type="button"
            onClick={resend}
            disabled={busy || cooldown > 0}
            className="kd9-btn-outline mt-3 flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm"
            data-testid="whatsapp-activation-resend"
          >
            <RefreshCw size={15} />
            {cooldown > 0 ? `Reenviar en ${cooldown}s` : sent ? "Reenviar código" : "Enviar código"}
          </button>
          <p className="mt-4 text-center text-xs opacity-55">
            {expiresIn > 0
              ? `El código actual vence en ${Math.floor(expiresIn / 60)}:${String(expiresIn % 60).padStart(2, "0")}. Solo el último código solicitado es válido.`
              : "El código tiene vigencia limitada. Si solicitas otro, el anterior deja de ser válido."}
          </p>
        </div>
      </div>
    </AuthShell>
  );
}
